import { BarChart3, Bot, Loader2, ShieldCheck, Tv } from "lucide-react";
import { useState } from "react";

function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

const POINTS = [
  { icon: BarChart3, title: "Build a dashboard in minutes", text: "Templates, drag-and-drop visuals and your SharePoint data — no laptop left running." },
  { icon: Tv, title: "Live on every office screen", text: "Review the draft, press Publish, and the TVs update within a minute." },
  { icon: Bot, title: "Let AI do the heavy lifting", text: "Claude or Codex can build pages for you. A person always reviews before anything goes live." },
];

export function LoginPage() {
  const [redirecting, setRedirecting] = useState(false);
  const error = new URLSearchParams(window.location.search).get("error");

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--bg)" }}>
      <section
        style={{
          flex: "1 1 56%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "48px clamp(32px, 6vw, 88px)",
          background: "radial-gradient(1200px 600px at 10% 10%, rgba(24,168,201,0.28), transparent 60%), linear-gradient(160deg, #071d3b 0%, #0b2f55 60%, #0e4d6b 100%)",
          color: "#fff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div className="ds-row" style={{ gap: 12, marginBottom: 36 }}>
          <span className="ds-sidebar__brand-mark" style={{ width: 40, height: 40, borderRadius: 11 }}>
            <BarChart3 size={22} />
          </span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 18 }}>Maridive Reports</div>
            <div style={{ fontSize: 12.5, opacity: 0.7 }}>Dashboards for every screen</div>
          </div>
        </div>
        <h1 style={{ color: "#fff", fontSize: "clamp(28px, 3.2vw, 42px)", lineHeight: 1.15, margin: "0 0 14px", maxWidth: 560, letterSpacing: "-0.01em" }}>
          The company's numbers, clear and live — in the browser and on every TV.
        </h1>
        <p style={{ fontSize: 16, opacity: 0.78, margin: "0 0 36px", maxWidth: 520 }}>Built in-house for Maridive. Easier than Power BI, and it publishes straight to the office screens.</p>
        <div className="ds-stack" style={{ gap: 18, maxWidth: 540 }}>
          {POINTS.map((p) => (
            <div key={p.title} className="ds-row" style={{ alignItems: "flex-start", gap: 14 }}>
              <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,0.1)", flexShrink: 0 }}>
                <p.icon size={19} />
              </span>
              <div>
                <div style={{ fontWeight: 650 }}>{p.title}</div>
                <div style={{ fontSize: 13.5, opacity: 0.72 }}>{p.text}</div>
              </div>
            </div>
          ))}
        </div>
        {/* decorative mini dashboard */}
        <div aria-hidden style={{ position: "absolute", right: -60, bottom: -40, width: 420, height: 250, borderRadius: 16, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", transform: "rotate(-6deg)", padding: 18, display: "grid", gridTemplateColumns: "repeat(4,1fr)", gridTemplateRows: "34px 1fr", gap: 10 }}>
          {[0, 1, 2, 3].map((i) => <div key={i} style={{ borderRadius: 6, background: "rgba(255,255,255,0.12)" }} />)}
          <div style={{ gridColumn: "span 3", borderRadius: 8, background: "rgba(255,255,255,0.08)", display: "flex", alignItems: "flex-end", gap: 8, padding: 12 }}>
            {[40, 70, 52, 90, 64, 110, 84].map((h, i) => <div key={i} style={{ flex: 1, height: h, borderRadius: 3, background: "rgba(77,216,255,0.55)" }} />)}
          </div>
          <div style={{ borderRadius: 8, background: "rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ width: 64, height: 64, borderRadius: "50%", border: "10px solid rgba(255,255,255,0.18)", borderTopColor: "rgba(45,212,191,0.8)" }} />
          </div>
        </div>
      </section>

      <section style={{ flex: "1 1 44%", display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}>
        <div className="ds-card" style={{ width: "100%", maxWidth: 400, padding: "36px 32px", boxShadow: "var(--shadow-md)" }}>
          <h2 style={{ margin: "0 0 6px", fontSize: 24 }}>Sign in</h2>
          <p className="ds-muted" style={{ margin: "0 0 26px" }}>Use your Maridive Microsoft 365 account — the same one as Outlook and Teams.</p>
          {error ? (
            <div className="ds-banner ds-banner--danger" style={{ marginBottom: 16 }}>
              <div className="ds-small">{error === "tenant" ? "Your organization isn't allowed to sign in yet. Ask IT to add it." : "Sign-in didn't complete. Please try again."}</div>
            </div>
          ) : null}
          <a
            href="/auth/login"
            onClick={() => setRedirecting(true)}
            className="ds-btn ds-btn--lg"
            style={{ width: "100%", textDecoration: "none", justifyContent: "center", pointerEvents: redirecting ? "none" : "auto", opacity: redirecting ? 0.7 : 1 }}
          >
            {redirecting ? <Loader2 size={18} className="ds-spin" /> : <MicrosoftIcon />}
            {redirecting ? "Opening Microsoft sign-in…" : "Sign in with Microsoft"}
          </a>
          <div className="ds-row ds-small ds-faint" style={{ marginTop: 22, gap: 8 }}>
            <ShieldCheck size={15} />
            Secure single sign-on. We never see your password.
          </div>
        </div>
      </section>
    </div>
  );
}
